/* iter196 — Payment disposition display helpers.
   ------------------------------------------------------------
   Single source of truth for turning a stored disposition/detail pair into
   the label the office actually sees. Reused across:
     - Admin invoice list (row column)
     - Admin invoice drawer
     - Field Completion Details (FCD panel)
     - Completion wizard closeout summary
   ------------------------------------------------------------ */

const PAID_OTHER_METHOD_LABEL = {
  quickbooks_pay: 'QB Pay',
  zelle: 'Zelle',
  already_paid: 'Already Paid',
};

const DISPOSITION_LABEL = {
  paid_cash:                  'Paid — Cash',
  paid_check:                 'Paid — Check',
  paid_card:                  'Paid — Card',
  paid_other:                 'Paid — Other',
  payment_link_sent:          'Payment Link Sent',
  paying_card_now:            'Paying Card Now',
  no_balance_due:             'No Balance Due',
  deferred_payment:           'Deferred Payment',
  deferred_unable_collect:    'Deferred / Unable to Collect',
};

const INVOICE_STATUS_LABEL = {
  create_invoice:            'Invoice Created',
  no_charge:                 'No Charge',
  no_customer_balance_due:   'No Balance Due',
  paid_field:                'Paid in Field',
  hsp_warranty_claim:        'HSP / Warranty',
  nexflow_warranty:          'NexFlow Warranty — No Charge',
  office_review_required:    'Office Review',
  save_draft:                'Draft',
  return_visit_needed:       'Return Visit',
  deferred_payment:          'Deferred Payment',
  deferred_payment_approved: 'Deferred (Approved)',
  deferred_unable_collect:   'Unable to Collect',
  customer_not_available:    'Customer Not Available',
  hsp:                       'HSP',
  warranty:                  'Warranty',
};

/**
 * iter196 — Format a "Paid — Other" pair as `PAID — QB Pay`, `PAID — Zelle`, etc.
 * Falls back to the raw detail if the value doesn't match a known slug.
 * Handles legacy free-text detail values (e.g., "Zelle", "QuickBooks Pay / GoPayment")
 * that were stored before the slug allow-list was introduced.
 */
export const formatPaymentDisposition = (disposition, methodDetail) => {
  if (!disposition) return '';
  const base = DISPOSITION_LABEL[disposition] || disposition.replace(/_/g, ' ');
  if (disposition !== 'paid_other') return base;
  if (!methodDetail) return base;
  const slug = String(methodDetail).trim().toLowerCase().replace(/[\s./-]+/g, '_');
  if (PAID_OTHER_METHOD_LABEL[slug]) return `Paid — ${PAID_OTHER_METHOD_LABEL[slug]}`;
  // Legacy free-text handling
  if (/quick.?books|qb.?pay|go.?payment/i.test(methodDetail)) return 'Paid — QB Pay';
  if (/zelle/i.test(methodDetail)) return 'Paid — Zelle';
  if (/already.?paid/i.test(methodDetail)) return 'Paid — Already Paid';
  return `Paid — ${methodDetail}`;
};

/** iter196 — Prettified invoice status. */
export const formatInvoiceStatus = (status) => {
  if (!status) return '';
  return INVOICE_STATUS_LABEL[status] || String(status).replace(/_/g, ' ');
};

/** iter196 — Best-effort payment display for an invoice row.
 * Prefers `payment_method_detail` when disposition is paid_other; otherwise
 * uses `payment_method` (card/cash/check) or the disposition label. */
export const formatInvoicePayment = (invoice) => {
  if (!invoice) return '';
  const pmd = invoice.payment_method_detail;
  if (pmd) {
    // pmd already stored on the invoice — treat as paid_other detail.
    return formatPaymentDisposition('paid_other', pmd);
  }
  const method = invoice.payment_method;
  if (method) return `Paid — ${String(method).charAt(0).toUpperCase()}${String(method).slice(1)}`;
  return '';
};

export default {
  formatPaymentDisposition,
  formatInvoiceStatus,
  formatInvoicePayment,
};
